import { EllipsisOutlined } from '@ant-design/icons'
import { Dropdown, Tag } from 'antd'
import type React from 'react'
import styled from 'styled-components'
import { Chevron } from '../../systemIcons'
import { ExpandableCardField, type ExpandableCardProps } from './types'

const ExpandableCard: React.FC<ExpandableCardProps> = ({
  id,
  title,
  status,
  date,
  titleIndicators,
  fields = [],
  menuItems,
  isExpanded = false,
  onToggleExpand,
  headerRightContent,
  detailsContent,
  testId,
  className
}) => {
  const hasDetails = fields.length > 0 || detailsContent
  const showExpandToggle = hasDetails && onToggleExpand

  const handleHeaderClick = () => {
    if (showExpandToggle) {
      onToggleExpand?.(id)
    }
  }

  const renderFields = () => {
    if (detailsContent) {
      return detailsContent
    }

    if (fields.length === 0) {
      return null
    }

    const fullWidthFields = fields.filter((f) => f.fullWidth)
    const gridFields = fields.filter((f) => !f.fullWidth)

    return (
      <>
        {fullWidthFields.length > 0 && (
          <FullWidthFieldsContainer>
            {fullWidthFields.map((field, index) => (
              <DetailRow key={`full-${index}`}>
                <DetailLabel>{field.label}</DetailLabel>
                <DetailValue>{field.value}</DetailValue>
              </DetailRow>
            ))}
          </FullWidthFieldsContainer>
        )}
        {gridFields.length > 0 && (
          <DetailsGrid>
            {gridFields.map((field, index) => (
              <DetailRow key={`grid-${index}`}>
                <DetailLabel>{field.label}</DetailLabel>
                <DetailValue>{field.value}</DetailValue>
              </DetailRow>
            ))}
          </DetailsGrid>
        )}
      </>
    )
  }

  return (
    <CardContainer className={className} data-testid={testId}>
      <CardHeader onClick={handleHeaderClick} $clickable={!!showExpandToggle}>
        <HeaderLeft>
          <TitleRow>
            <Title>{title}</Title>
            {titleIndicators}
          </TitleRow>
          <HeaderMeta>
            {status && (
              <StyledTag color={status.color || 'default'}>
                {status.label}
              </StyledTag>
            )}
            {date && <DateText>{date}</DateText>}
          </HeaderMeta>
        </HeaderLeft>
        <HeaderRight>
          {headerRightContent}
          {!headerRightContent && showExpandToggle && (
            <ExpandButton>
              <Chevron direction={isExpanded ? 'up' : 'down'} />
            </ExpandButton>
          )}
          {menuItems && menuItems.length > 0 && (
            <Dropdown
              menu={{ items: menuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <ActionButton onClick={(e) => e.stopPropagation()}>
                <EllipsisOutlined />
              </ActionButton>
            </Dropdown>
          )}
        </HeaderRight>
      </CardHeader>

      {isExpanded && hasDetails && (
        <DetailsSection>{renderFields()}</DetailsSection>
      )}
    </CardContainer>
  )
}

export default ExpandableCard

const CardContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
`

const CardHeader = styled.div<{ $clickable: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 20px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Title = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
`

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StyledTag = styled(Tag)`
  text-transform: capitalize;
  font-weight: normal;
  margin: 0;
`

const DateText = styled.span`
  font-size: 13px;
  color: #666;
`

const ExpandButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: #999;
  font-size: 12px;
`

const ActionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  color: #666;

  &:hover {
    background: #f5f5f5;
  }
`

const DetailsSection = styled.div`
  padding: 0 20px 20px 20px;
  border-top: 1px solid #f0f0f0;
  margin-top: -4px;
  padding-top: 16px;
`

const FullWidthFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
`

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
  line-height: 1.5;
`
